// NPM Imports
import React from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@revvsales/components";
import { cloneDeep } from "lodash";

// Components Imports
import DocumentBlocks from "../DocumentBlocks";
import { DOCUMENTS } from "../../../constants";
import ContentLock from "../../DocumentItems/ContentLock";
import { DOCUMENT_BLOCKS } from "../constants";

// Actions Imports
import EditorActions from "../../../actions/EditorActions";
import {
  coverpageThemeAction,
  updateLayoutInsideComponent,
} from "../../../actions/DocumentActions";

// Internal Imports
import {
  headerFooter as dummyHeaderFooterComp,
  emptyContainer,
  cloneComponent,
} from "../../../utils/documentUtils";
import ToggleComponentLockCommand from "../../../commands/toggleComponentLockCommand";
import ReOrderComponentInHeaderCommand from "../../../commands/reOrderComponentInHeaderCommand";

// Destructuring
const { nukeEditorState } = EditorActions;
const {
  DOCUMENT_MODE,
  COMPONENT_FEATURE,
  DOCUMENT_COMPONENT_TYPES,
} = DOCUMENTS;

class DocumentHeaderView extends React.Component {
  _onPlaceholderDrop = (rowIndex, colIndex, newRow, dbRowIndex, dbColIndex) => {
    const {
      draggedSection,
      draggedFrom,
      draggedRowIndex,
      draggedColumnIndex,
    } = this.props;

    if (draggedFrom === "header-footer") {
      this._addComponentInHeader(
        this.props.draggedSection,
        rowIndex,
        colIndex,
        newRow,
        dbRowIndex,
        dbColIndex,
      );
      this.props.clearDragDropData();
    } else {
      const { layout, component } = this.props;
      const reOrderCommand = new ReOrderComponentInHeaderCommand({
        mode: this.props.mode,
        layout: cloneDeep(layout),
        newRow,
        rowIndex,
        colIndex,
        draggedSection,
        updateComponent: this.props.updateComponent,
        component: cloneDeep(component),
        components: cloneDeep(this.props.components),
        draggedRowIndex,
        draggedColumnIndex,
        draggedFrom,
        draggedTo: "header",
        updateDraggedSection: this.props.updateDraggedSection,
        updateLayoutInsideComponent: this.props.updateLayoutInsideComponent,
      });
      this.props.executeCommand(reOrderCommand);
    }
  };

  _enableHeader = e => {
    e.stopPropagation();
    this.props.updateAddComponentinToolbarAction(
      DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER,
    );
    this.props.coverpageThemeAction(null);
    // If Header/Footer is not there then, first add and then activate it
    if (!this.props.component) {
      const newSection = dummyHeaderFooterComp(this.props.mode);
      this.props.addComponent(newSection, 0, 0, true, 0, 0, "HEADER");
    }
  };

  _addComponentInHeader = (component, rowIndex, colIndex, newRow) => {
    if (component) {
      const { layout, components } = this.props;
      if (newRow) {
        layout.splice(rowIndex, 0, [component.id]);
      } else if (!newRow) {
        const rowIDs = [...layout[rowIndex]];
        rowIDs.splice(colIndex, 0, component.id);
        layout[rowIndex] = rowIDs;
      }
      let { component: headerComponent } = this.props;
      const oldHeaderFooterComponent = { ...headerComponent };
      components[component.id] = { content: component };
      headerComponent = {
        ...headerComponent.content,
        data: {
          ...headerComponent.content.data,
          header: {
            ...headerComponent.content.data.header,
            layout,
            components,
          },
        },
      };

      this.props.updateComponent(
        headerComponent,
        true,
        false,
        { ...this.props.component, oldHeaderFooterComponent },
        component,
      );
    }
  };

  _cloneComponentInHeader = (oldComponent, rowIndex, colIndex, newRow) => {
    const clonedComponent = cloneComponent(oldComponent, this.props.mode);
    this._addComponentInHeader(clonedComponent, rowIndex + 1, colIndex, newRow);
  };

  _deleteComponentInHeader = (componentID, component) => {
    const { layout, components } = this.props;
    let { component: headerComponent } = this.props;
    const oldHeaderFooterComponent = { ...headerComponent };
    const newLayout = layout
      .map(row => row.filter(id => id !== componentID))
      .filter(row => row.length > 0);
    delete components[componentID];

    headerComponent = {
      ...headerComponent.content,
      data: {
        ...headerComponent.content.data,
        header: {
          ...headerComponent.content.data.header,
          layout: newLayout,
          components,
        },
      },
    };
    this.props.updateComponent(headerComponent, true, false, {
      ...component,
      oldHeaderFooterComponent,
    });
    this.props.nukeEditorState(true);
  };

  _updateComponentInHeader = (
    updatedSection,
    change,
    isMouseHover,
    oldSection,
    addedNewTextSection,
  ) => {
    if (!isMouseHover && oldSection && this.props.component) {
      const { component } = this.props;
      const oldHeaderFooterComponent = { ...component };
      const headerComponent = {
        ...component.content,
        data: {
          ...component.content.data,
          header: {
            ...component.content.data.header,
            components: {
              ...component.content.data.header.components,
              [updatedSection.id]: {
                ...component.content.data.header.components[updatedSection.id],
                content: updatedSection,
              },
            },
          },
        },
      };
      this.props.updateComponent(
        headerComponent,
        change,
        isMouseHover,
        { ...oldSection, oldHeaderFooterComponent },
        addedNewTextSection,
      );
    }
  };

  _handleComponentLockUnlock = (section, lockStatus) => {
    if (section && this.props.component) {
      const toggleComponentLockCommand = new ToggleComponentLockCommand({
        toggleComponentLockUnlock: this.props.toggleComponentLockUnlock,
        payload: {
          lockStatus,
          activeComponentID: section.id,
          activeComponentType: section.type,
          oldSection: this.props.component.content,
          isHeaderView: true,
          headerComponentID: this.props.componentId,
        },
      });
      this.props.executeCommand(toggleComponentLockCommand);
    }
  };

  _handleHeaderLockUnlock = (event, flag) => {
    event.preventDefault();
    this.props.toggleBlockLockUnlock(this.props.component, flag);
  };

  // Lock Feature
  _lockButton = () => {
    const { clauseID } = this.props.component;

    let lockView = null;
    // for document, show only locked section because of added clause
    if (this.props.mode === DOCUMENT_MODE.DOCUMENT) {
      lockView = clauseID ? (
        <Tooltip
          placement="left"
          overlay="This clause is locked for any editing.">
          <span className="btn-icon locked-icon">
            <FontAwesomeIcon icon={["far", "lock"]} />
          </span>
        </Tooltip>
      ) : null;
    } else if (COMPONENT_FEATURE[this.props.mode].lockSection) {
      lockView = (
        <ContentLock
          toggleSectionLock={this._handleHeaderLockUnlock}
          toggleUpgradePopup={this.props.toggleUpgradePopup}
          section={this.props.component.content}
          {...this.props}
        />
      );
    }
    return lockView;
  };

  // delete feature
  // _deleteButton = () =>
  //   !(
  //     !COMPONENT_FEATURE[this.props.mode].deleteSection ||
  //     this.props.component.content.lock
  //   ) ? (
  //     <Tooltip
  //       placement="top"
  //       // overlay={`Delete ${this.props.activeSectionType}`}
  //       overlay="Delete Header"
  //       align={{ offset: [0, 3] }}>
  //       <span
  //         data-test="documentitems-delete-the-content-btn"
  //         className="btn-icon"
  //         id="delete-the-content-btn"
  //         onClick={this._deleteHeaderComponent}
  //         onKeyDown={() => {}}
  //         role="button"
  //         tabIndex={0}>
  //         <FontAwesomeIcon icon={["far", "trash-alt"]} />
  //       </span>
  //     </Tooltip>
  //   ) : null;

  _decideSectionActionBtns = () => {
    const actionBtns =
      !this.props.isLocked && this.props.component ? (
        <div
          className="style-component-wrapper"
          data-test="documentitems-component-wrapper-template-component-doc-view">
          <div
            className="style-component-action"
            data-test="documentitems-component-action-template-component-doc-view">
            {this._lockButton()}
            {/* {this._deleteButton()} */}
          </div>
        </div>
      ) : null;
    return actionBtns;
  };

  _getClassName = () => {
    const { isLocked, component } = this.props;
    let className = "style-component headerfooter-component ";
    if (!isLocked && component && component.content && component.content.lock) {
      className += " locked-style-component ";
    }
    if (this.props.activeSectionType === DOCUMENT_BLOCKS.HEADER) {
      className += " active ";
    }
    return className;
  };

  // get the header view
  _getView = () => {
    // check if the document has a header & footer section or not
    const emptyComponent = emptyContainer();
    let decideComponents = null;
    let decideLayout = null;
    let emptyBlockConfig = null;
    // Get the "components" & "layout" as an emptyComponent in case of empty document
    if (this.props.components && Object.keys(this.props.components).length) {
      decideComponents = this.props.components;
    } else {
      emptyBlockConfig = {
        flag: true,
        type: "HEADER",
      };
      decideComponents = emptyComponent
        ? {
            [emptyComponent.id]: {
              row: undefined,
              col: undefined,
              content: emptyComponent,
            },
          }
        : null;
    }
    if (
      Array.isArray(this.props.layout) &&
      this.props.layout[0] &&
      this.props.layout[0].length > 0
    ) {
      decideLayout = this.props.layout;
    } else {
      emptyBlockConfig = {
        flag: true,
        type: "HEADER",
      };
      decideLayout = emptyComponent ? [[emptyComponent.id]] : null;
    }
    if (decideComponents && decideLayout) {
      const componentMeta = {
        blockType: DOCUMENT_BLOCKS.HEADER,
        itemType: DOCUMENT_BLOCKS.HEADER,
        headerId: this.props.componentId,
      };
      let lockedStatus = false;
      if (this.props.component && this.props.component.content) {
        lockedStatus = this.props.component.content.lock;
      }

      let className = "document-header-view";

      if (this.props.draggedSection && (this.props.isLocked || lockedStatus)) {
        className += " disable-component";
      }
      return (
        <div className={className}>
          <DocumentBlocks
            layout={decideLayout}
            components={decideComponents}
            id={this.props.componentId}
            emptyBlockConfig={emptyBlockConfig}
            blockType={DOCUMENT_BLOCKS.HEADER}
            itemType={DOCUMENT_COMPONENT_TYPES.HEADER}
            componentMeta={componentMeta}
            mode={this.props.mode}
            isLocked={this.props.isLocked || lockedStatus}
            isActiveHeaderFooter={this.props.isActiveHeaderFooter}
            addComponent={this._addComponentInHeader}
            updateComponent={this._updateComponentInHeader}
            deleteComponent={this._deleteComponentInHeader}
            cloneSection={this._cloneComponentInHeader}
            // setDraggedSection={this.props.setDraggedSection}
            removeHighlightSelection={this.props.removeHighlightSelection}
            updateHoveredSectionId={this.props.updateHoveredSectionId}
            removeActiveComponents={this.props.removeActiveComponents}
            showDocumentDiffView={this.props.showDocumentDiffView}
            deleteSection={this.props.deleteSection}
            isSharedDocument={this.props.isSharedDocument}
            isDocumentLocked={this.props.isDocumentLocked}
            updateTemplateDocSectionLockUnlock={
              this.props.updateTemplateDocSectionLockUnlock
            }
            handleComponentLockUnlock={this._handleComponentLockUnlock}
            // Ref related props
            customToolbarRef={this.props.customToolbarRef}
            setRefsByType={this.props.setRefsByType}
            // ------- Drag & Drop related props -------
            draggedSection={this.props.draggedSection}
            draggedPosition={this.props.draggedPosition}
            dragggedOverData={this.props.dragggedOverData}
            onPlaceholderDragEnter={this.props.onPlaceholderDragEnter}
            onPlaceholderDragLeave={this.props.onPlaceholderDragLeave}
            onPlaceholderDrop={this._onPlaceholderDrop}
            tableColorpickerDropdownRef={this.props.tableColorpickerDropdownRef}
            match={this.props.match}
          />
        </div>
      );
    }
    return null;
  };

  render() {
    return (
      <div id={`document-header-${this.props.componentId}`}>
        {/* -------- Overlay Section --------*/}
        {!this.props.isLocked && (
          <div
            className="header-footer-helptext"
            role="button"
            tabIndex={0}
            onDoubleClick={e => this._enableHeader(e, DOCUMENT_BLOCKS.HEADER)}
            onKeyDown={() => {}}>
            Double click to add or modify header
          </div>
        )}
        <div className={this._getClassName()}>
          {/* -------- Action Buttons Section --------*/}
          {!this.props.isLocked &&
            this.props.addComponentType ===
              DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER &&
            this._decideSectionActionBtns()}
          {/* -------- Main View Section --------*/}
          {this._getView()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { componentId } = props;
  const { components } = state.DocumentStore;
  let headerLayout = [[]];
  let headerComponents = {};

  const headerfooter = componentId && components[componentId];
  if (
    headerfooter &&
    headerfooter.content &&
    headerfooter.content.data &&
    headerfooter.content.data.header
  ) {
    if (Array.isArray(headerfooter.content.data.header.layout))
      headerLayout = JSON.parse(
        JSON.stringify(headerfooter.content.data.header.layout),
      );
    headerComponents = headerfooter.content.data.header.components;
  }
  return {
    layout: headerLayout,
    components: headerComponents,
    component: headerfooter,
  };
};

const mapDispatchToProps = {
  nukeEditorState,
  coverpageThemeAction,
  updateLayoutInsideComponent,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentHeaderView);



// WEBPACK FOOTER //
// ./src/components/DocumentV2/DocumentHeaderView/DocumentHeaderView.js